import {useState} from "react";
import {EmployeeModel, EmploymentContractModel, Money} from "../../redux/types";
import useEmployeeActions from "../../hooks/useEmployeeActions";
import {useAppSelector} from "../../redux/hooks";
import {formatMoney} from "../../utils/moneyHelper";

export type ContractRow = EmployeeModel & { selectedContract: EmploymentContractModel };

interface ContractRowColumnDefinition {
  title: string
  extractor: (row: ContractRow) => string
}

const moneyToString = (m?: Money) => m ? formatMoney(m) : ''

const useEmployeeCsvExport = () => {
  const {fetchEmployees} = useEmployeeActions()
  const optOuts = useAppSelector(state => state.optOut.optOuts)
  const [exporting, setExporting] = useState(false)

  const columns: ContractRowColumnDefinition[] = [
    {
      title: 'Given Name',
      extractor: (item: EmployeeModel) => item.givenName || ''
    },
    {
      title: 'Family Name',
      extractor: (item: EmployeeModel) => item.familyName || ''
    },
    {
      title: 'External ID',
      extractor: (item: EmployeeModel) => item.externalId || ''
    },
    {
      title: 'Employment Number',
      extractor: (item: EmployeeModel) => item.employmentNumber || ''
    },
    {
      title: 'Identification Number',
      extractor: (item: EmployeeModel) => item.identificationNumber || ''
    },
    {
      title: 'Email',
      extractor: (item: EmployeeModel) => item.email || ''
    },
    {
      title: 'Phone Number',
      extractor: (item: EmployeeModel) => item.phoneNumber || ''
    },
    {
      title: 'Type',
      extractor: (item: EmployeeModel) => item.type
    },
    {
      title: 'Connected User',
      extractor: (item: EmployeeModel) => item.connectedUser ? `${item.connectedUser.givenName} ${item.connectedUser.familyName}` : ''
    },
    {
      title: 'Employer Name',
      extractor: (item: EmployeeModel) => item.employerName
    },
    {
      title: 'Bank Account Number',
      extractor: (item: EmployeeModel) => item.bankAccountNumber || ''
    },
    {
      title: 'Department',
      extractor: (item: EmployeeModel) => item.department || ''
    },
    {
      title: 'Category',
      extractor: (item: EmployeeModel) => item.category || ''
    },
    {
      title: 'Location',
      extractor: (item: EmployeeModel) => item.location || ''
    },
    {
      title: 'Cost Center',
      extractor: (item: EmployeeModel) => item.costCenter || ''
    },
    {
      title: 'Payroll File Identifier',
      extractor: (item: EmployeeModel) => item.payrollFileIdentifier || ''
    },
    {
      title: 'Activated',
      extractor: (item: EmployeeModel) => item.activated ? 'Yes' : 'No'
    },
    {
      title: 'Activation Date',
      extractor: (item: EmployeeModel) => item.activationDate || ''
    },
    {
      title: 'Extend Termination To Upcoming Payday',
      extractor: (item: EmployeeModel) => item.extendTerminationToUpcomingPayday ? 'Yes' : 'No'
    },
    {
      title: 'Paused',
      extractor: (item: EmployeeModel) => item.paused ? 'Yes' : 'No'
    },
    {
      title: 'Pause Reason',
      extractor: (item: EmployeeModel) => item.pauseReason || ''
    },
    {
      title: 'Pause Date',
      extractor: (item: EmployeeModel) => item.pauseDate || ''
    },
    {
      title: 'Non Eligible',
      extractor: (item: EmployeeModel) => item.nonEligible ? 'Yes' : 'No'
    },
    {
      title: 'Under Foreclosure',
      extractor: (item: EmployeeModel) => item.underForeclosure ? 'Yes' : 'No'
    },
    {
      title: 'Pending Bank Account Activation',
      extractor: (item: EmployeeModel) => item.pendingBankAccountActivation ? 'Yes' : 'No'
    },
    {
      title: 'Bank Account Activation Failed',
      extractor: (item: EmployeeModel) => item.bankAccountActivationFailed ? 'Yes' : 'No'
    },
    {
      title: 'Tax Settings',
      extractor: (item: EmployeeModel) => JSON.stringify(item.taxSettings)
    },
    {
      title: 'Status',
      extractor: (item: EmployeeModel) => item.status
    },
    {
      title: 'Payout Settings',
      extractor: (item: EmployeeModel) => JSON.stringify(item.payoutSettings)
    },
    {
      title: 'Last Pay Data Update',
      extractor: (item: EmployeeModel) => item.lastPayDataUpdate || ''
    },
    {
      title: 'Is Eligible For Activation Reminder',
      extractor: (item: EmployeeModel) => item.isEligibleForActivationReminder ? 'Yes' : 'No'
    },
    {
      title: 'Welcome Message Sent',
      extractor: (item: EmployeeModel) => item.welcomeMessageSent ? 'Yes' : 'No'
    },
    {
      title: 'Last Activation Reminder Date',
      extractor: (item: EmployeeModel) => item.lastActivationReminderDate || ''
    },
    {
      title: 'Next Activation Reminder Date',
      extractor: (item: EmployeeModel) => item.nextActivationReminderDate || ''
    },
    {
      title: 'Opt Out',
      extractor: (item: EmployeeModel) => {
        if (!optOuts) return '?'

        const employeeOptOut = optOuts.employeeIds.find(optOut => optOut === item.id)
        const userOptOut = optOuts.userIds.find(optOut => optOut === item.connectedUser?.id)

        return employeeOptOut || userOptOut ? 'Yes' : 'No'
      }
    },

    // Contract-specific fields
    {
      title: 'Contract Id',
      extractor: (row: ContractRow) => row.selectedContract?.id || ''
    },
    {
      title: 'Contract External ID',
      extractor: (row: ContractRow) => row.selectedContract?.externalId || ''
    },
    {
      title: 'Contract Report ID',
      extractor: (row: ContractRow) => row.selectedContract?.reportId || ''
    },
    {
      title: 'Contract Description',
      extractor: (row: ContractRow) => row.selectedContract?.description || ''
    },
    {
      title: 'Collective Agreement',
      extractor: (row: ContractRow) => row.selectedContract?.collectiveAgreement || ''
    },
    {
      title: 'Position',
      extractor: (row: ContractRow) => row.selectedContract?.position || ''
    },
    {
      title: 'Pay Schedule Identifier',
      extractor: (row: ContractRow) => row.selectedContract?.payScheduleIdentifier || ''
    },
    {
      title: 'Pay Calculation Type',
      extractor: (row: ContractRow) => row.selectedContract?.payCalculationType || ''
    },
    {
      title: 'Contract Start Date',
      extractor: (row: ContractRow) => row.selectedContract?.startDateString || ''},
    {
      title: 'Contract End Date',
      extractor: (row: ContractRow) => row.selectedContract?.endDateString || ''},
    {
      title: 'Contract Terminated',
      extractor: (row: ContractRow) => row.selectedContract?.terminated ? 'Yes' : 'No'},
    {
      title: 'Handle Vacation Pay',
      extractor: (row: ContractRow) => row.selectedContract?.handleVacationPay ? 'Yes' : 'No'
    },
    {
      title: 'Vacation Pay Percent',
      extractor: (row: ContractRow) => (row.selectedContract?.vacationPayPercent ?? '') + ''
    },
    {
      title: 'Current Salary',
      extractor: (row: ContractRow) => moneyToString(row.selectedContract?.currentSalary)},
    {
      title: 'Current Hourly Wage',
      extractor: (row: ContractRow) => moneyToString(row.selectedContract?.currentHourlyWage)
    }
  ];

  const exportEmployees = async (employerId: string | null, searchTerm: string | null) => {

    const convertToCSV = (rows: ContractRow[]): string => {
      const headers = columns.map(col => col.title).join(';');
      const lines = rows.map(row => columns.map(col => col.extractor(row)).join(';'))
      return `${headers}\n${lines.join('\n')}`;
    };

    try {
      setExporting(true);
      const employees = await fetchEmployees(employerId, searchTerm ?? '');

      if (employees.length === 0) {
        console.warn('No employees found to export');
        return;
      }

      // Transform to one row per contract
      const rows: ContractRow[] = employees
        .flatMap(e => e.employmentContracts.map(c => ({...e, selectedContract: c} as ContractRow)));

      if (rows.length === 0) {
        console.warn('No employment contracts found to export');
        return;
      }

      const csvContent = convertToCSV(rows);
      // Add UTF-8 BOM to help Excel recognize the encoding
      const bom = '\uFEFF';
      const blob = new Blob([bom + csvContent], {type: 'text/csv;charset=utf-8;'});
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = "employees.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting employees:', error);
    } finally {
      setExporting(false);
    }
  }

  return {exporting, exportEmployees}
}

export default useEmployeeCsvExport
